// ==================================================================================
// Digital Approval System (DAS) - Consolidation Hooks
// ==================================================================================
// Custom hooks for Two-Level Approval / Consolidation operations
// ==================================================================================

import { useState, useCallback, useEffect, useMemo } from 'react';
import { consolidationService } from '../../../api/services/das/consolidationService';
import {
  PreApproveRequest,
  PreApproveResponse,
  CreateConsolidatedRequestRequest,
  CreateConsolidatedRequestResponse,
  AddToConsolidationRequest,
  RemoveFromConsolidationRequest,
  ConsolidationUpdateResponse,
  FinalApproveRequest,
  FinalRejectRequest,
  FinalApprovalResponse,
  ConsolidatedRequestSummary,
  PreApprovedRequest,
  PreApprovedRequestQueryParameters,
  PreApprovedRequestListResponse,
  ConsolidatedRequestListResponse,
  TwoLevelApprovalStatistics,
  ValidateConsolidationRequest,
  ValidateConsolidationResponse,
  UndoConsolidationRequest,
  UndoConsolidationResponse,
  ConsolidationWorkflowConfig,
} from '../../../api/types/das/consolidation.types';
import { Request } from '../../../api/types/requests.types';
import { PaginationInfo } from '../../../api/types/requests.types';

// ==================================================================================
// PRE-APPROVAL HOOKS
// ==================================================================================

/**
 * Hook for pre-approval action operations
 */
export const usePreApproval = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Pre-approve a request
   */
  const preApprove = async (
    requestId: string,
    request: PreApproveRequest
  ): Promise<PreApproveResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.preApprove(requestId, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pre-approve request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if current user can pre-approve a request
   */
  const checkCanPreApprove = async (requestId: string): Promise<boolean> => {
    try {
      return await consolidationService.canPreApprove(requestId);
    } catch {
      return false;
    }
  };

  return {
    loading,
    error,
    preApprove,
    checkCanPreApprove,
  };
};

/**
 * Hook for fetching pre-approved requests available for consolidation
 */
export const usePreApprovedRequests = (params?: PreApprovedRequestQueryParameters) => {
  const [data, setData] = useState<PreApprovedRequestListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const fetchPreApproved = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getPreApproved(params);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pre-approved requests';
      setError(message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchPreApproved();
  }, [fetchPreApproved]);

  const pagination: PaginationInfo | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNext,
      hasPreviousPage: data.hasPrevious,
    };
  }, [data]);

  return {
    data,
    items: data?.items || [],
    pagination,
    loading,
    error,
    refetch: fetchPreApproved,
  };
};

/**
 * Hook for fetching pre-approved requests by request type
 */
export const usePreApprovedByType = (
  requestTypeId: string | null,
  excludeAlreadyConsolidated = true
) => {
  const [data, setData] = useState<PreApprovedRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchByType = useCallback(async () => {
    if (!requestTypeId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getPreApprovedByType(
        requestTypeId,
        excludeAlreadyConsolidated
      );
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pre-approved requests';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [requestTypeId, excludeAlreadyConsolidated]);

  useEffect(() => {
    fetchByType();
  }, [fetchByType]);

  return {
    data,
    loading,
    error,
    refetch: fetchByType,
  };
};

// ==================================================================================
// CONSOLIDATION MANAGEMENT HOOKS
// ==================================================================================

/**
 * Hook for consolidation CRUD operations
 */
export const useConsolidationActions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a consolidated approval request
   */
  const createConsolidation = async (
    request: CreateConsolidatedRequestRequest
  ): Promise<CreateConsolidatedRequestResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.create(request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create consolidated request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add items to an existing consolidation
   */
  const addToConsolidation = async (
    id: string,
    request: AddToConsolidationRequest
  ): Promise<ConsolidationUpdateResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.addItems(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add items to consolidation';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove items from a consolidation
   */
  const removeFromConsolidation = async (
    id: string,
    request: RemoveFromConsolidationRequest
  ): Promise<ConsolidationUpdateResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.removeItems(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove items from consolidation';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate requests for consolidation
   */
  const validateForConsolidation = async (
    request: ValidateConsolidationRequest
  ): Promise<ValidateConsolidationResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.validate(request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to validate consolidation';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createConsolidation,
    addToConsolidation,
    removeFromConsolidation,
    validateForConsolidation,
  };
};

/**
 * Hook for fetching a consolidated request summary
 */
export const useConsolidatedRequest = (id: string | null) => {
  const [data, setData] = useState<ConsolidatedRequestSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConsolidation = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getById(id);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch consolidated request';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchConsolidation();
  }, [fetchConsolidation]);

  return {
    data,
    loading,
    error,
    refetch: fetchConsolidation,
  };
};

/**
 * Hook for fetching all consolidated requests
 */
export const useConsolidatedRequests = (
  status?: string,
  pageNumber = 1,
  pageSize = 20
) => {
  const [data, setData] = useState<ConsolidatedRequestListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConsolidations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getAll(status, pageNumber, pageSize);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch consolidated requests';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [status, pageNumber, pageSize]);

  useEffect(() => {
    fetchConsolidations();
  }, [fetchConsolidations]);

  const pagination: PaginationInfo | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNext,
      hasPreviousPage: data.hasPrevious,
    };
  }, [data]);

  return {
    data,
    items: data?.items || [],
    pagination,
    loading,
    error,
    refetch: fetchConsolidations,
  };
};

/**
 * Hook for fetching child requests of a consolidation
 */
export const useConsolidationChildren = (consolidatedRequestId: string | null) => {
  const [data, setData] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChildren = useCallback(async () => {
    if (!consolidatedRequestId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getChildren(consolidatedRequestId);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch child requests';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [consolidatedRequestId]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  return {
    data,
    loading,
    error,
    refetch: fetchChildren,
  };
};

// ==================================================================================
// FINAL APPROVAL HOOKS
// ==================================================================================

/**
 * Hook for final approval operations
 */
export const useFinalApproval = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Give final approval to a consolidated request
   */
  const finalApprove = async (
    id: string,
    request: FinalApproveRequest
  ): Promise<FinalApprovalResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.finalApprove(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve consolidated request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reject a consolidated request
   */
  const finalReject = async (
    id: string,
    request: FinalRejectRequest
  ): Promise<FinalApprovalResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.finalReject(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject consolidated request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if current user can give final approval
   */
  const checkCanFinalApprove = async (id: string): Promise<boolean> => {
    try {
      return await consolidationService.canFinalApprove(id);
    } catch {
      return false;
    }
  };

  return {
    loading,
    error,
    finalApprove,
    finalReject,
    checkCanFinalApprove,
  };
};

// ==================================================================================
// VALIDATION HOOKS
// ==================================================================================

/**
 * Hook for checking consolidation status of a request
 */
export const useConsolidationStatus = (requestId: string | null) => {
  const [isConsolidated, setIsConsolidated] = useState<boolean | null>(null);
  const [isInConsolidation, setIsInConsolidation] = useState<boolean | null>(null);
  const [parentRequest, setParentRequest] = useState<Request | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    if (!requestId) {
      setIsConsolidated(null);
      setIsInConsolidation(null);
      setParentRequest(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [consolidated, inConsolidation, parent] = await Promise.all([
        consolidationService.isConsolidated(requestId),
        consolidationService.isInConsolidation(requestId),
        consolidationService.getParent(requestId),
      ]);
      setIsConsolidated(consolidated);
      setIsInConsolidation(inConsolidation);
      setParentRequest(parent);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check consolidation status';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return {
    isConsolidated,
    isInConsolidation,
    parentRequest,
    loading,
    error,
    refetch: checkStatus,
  };
};

// ==================================================================================
// STATISTICS HOOKS
// ==================================================================================

/**
 * Hook for fetching two-level approval statistics
 */
export const useConsolidationStatistics = (entityId?: string) => {
  const [data, setData] = useState<TwoLevelApprovalStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getStatistics(entityId);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch statistics';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    data,
    loading,
    error,
    refetch: fetchStatistics,
  };
};

/**
 * Hook for fetching pending consolidation count
 */
export const usePendingConsolidationCount = (requestTypeId?: string) => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getPendingConsolidationCount(requestTypeId);
      setCount(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch count';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [requestTypeId]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return {
    count,
    loading,
    error,
    refetch: fetchCount,
  };
};

/**
 * Hook for fetching pending final approval count
 */
export const usePendingFinalApprovalCount = () => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getPendingFinalApprovalCount();
      setCount(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch count';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return {
    count,
    loading,
    error,
    refetch: fetchCount,
  };
};

// ==================================================================================
// UNDO CONSOLIDATION HOOKS
// ==================================================================================

/**
 * Hook for undo consolidation operations
 */
export const useUndoConsolidation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Undo a consolidation - reverts all child requests back to Pre Approved status
   */
  const undoConsolidation = async (
    id: string,
    request: UndoConsolidationRequest
  ): Promise<UndoConsolidationResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.undo(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to undo consolidation';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if current user can undo a consolidation
   */
  const checkCanUndo = async (id: string): Promise<boolean> => {
    try {
      return await consolidationService.canUndo(id);
    } catch {
      return false;
    }
  };

  return {
    loading,
    error,
    undoConsolidation,
    checkCanUndo,
  };
};

// ==================================================================================
// CONFIGURATION HOOKS
// ==================================================================================

/**
 * Hook for fetching consolidation workflow configuration
 */
export const useConsolidationConfig = (enterpriseId?: string, entityId?: string) => {
  const [data, setData] = useState<ConsolidationWorkflowConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.getConfig(enterpriseId, entityId);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch configuration';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, entityId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    data,
    requireConsolidationForFinalApproval: data?.requireConsolidationForFinalApproval ?? false,
    loading,
    error,
    refetch: fetchConfig,
  };
};

/**
 * Hook for checking if consolidation is required before final approval
 */
export const useIsConsolidationRequired = (enterpriseId?: string, entityId?: string) => {
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkIsRequired = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await consolidationService.isConsolidationRequired(enterpriseId, entityId);
      setIsRequired(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check consolidation requirement';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, entityId]);

  useEffect(() => {
    checkIsRequired();
  }, [checkIsRequired]);

  return {
    isRequired,
    loading,
    error,
    refetch: checkIsRequired,
  };
};
